import React from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Grid,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PaymentsIcon from "@mui/icons-material/Payments";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UpdateIcon from "@mui/icons-material/Update";
import bgFooterImg from "../../assets/img/footer-back.jpg";

const sections = [
    {
        icon: <GavelIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "1. Acceptance of Terms",
        content:
            "By registering, downloading, or playing Critical Conquer, you automatically agree to follow all server rules and terms. Failure to comply may result in warnings, suspensions, mutes, or permanent bans depending on the severity of the violation.",
    },
    {
        icon: <AccountCircleIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "2. Account Responsibility",
        content:
            "Players are fully responsible for their own accounts, passwords, and items. Staff members will never ask for your password. Sharing accounts is allowed at your own risk, and lost items or characters due to sharing, scams, or incorrect trades will not be refunded or restored.",
    },
    {
        icon: <PaymentsIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "3. Donations & Payments",
        content:
            "All donations made to Critical Conquer are voluntary and non-refundable. Donations help support server hosting, development, protection, and future updates. We do not compensate for downtime, bugs, disconnects, or account losses.",
    },
    {
        icon: <SecurityIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "4. Game Rules",
        content:
            "Using cheats, bots, macros, exploits, or third-party software is strictly prohibited and may result in a permanent ban without warning. Black Name and Red Name item drops are not refundable. Private RMT is allowed at your own risk, but advertising RMT in World Chat, Broadcasts, or public channels is prohibited and may result in a temporary mute or suspension.",
    },
    {
        icon: <WarningAmberIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "5. Fair Play & Punishments",
        content:
            "Temporary bans or mutes must be respected. Attempting to bypass punishments using alternate accounts may result in longer penalties or permanent suspension. If you believe a punishment was issued unfairly, you may respectfully contact a staff member for review.",
    },
    {
        icon: <UpdateIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "6. Changes to Terms",
        content:
            "Critical Conquer reserves the right to update or modify these Terms and Conditions at any time without prior notice. Continued use of the server after updates means you accept the revised terms.",
    },
];

const Terms = () => {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundImage: `
                    linear-gradient(
                        rgba(5,5,5,0.92),
                        rgba(5,5,5,0.95)
                    ),
                    url(${bgFooterImg})
                `,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                py: { xs: 5, md: 10 },
                px: 2,
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box textAlign="center" mb={6}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: "2.2rem",
                                sm: "3rem",
                                md: "4rem",
                            },
                            mb: 2,
                            letterSpacing: 1,
                            background:
                                "linear-gradient(90deg, #ffd786 0%, #b88942 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            textShadow: "0px 0px 25px rgba(255,215,134,0.25)",
                        }}
                    >
                        Terms & Conditions
                    </Typography>

                    <Typography
                        sx={{
                            color: "#bdbdbd",
                            maxWidth: 700,
                            mx: "auto",
                            fontSize: {
                                xs: "0.95rem",
                                md: "1.1rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        Welcome to Critical Conquer. Please read these terms
                        carefully before playing. Our goal is to maintain a fair,
                        competitive, and enjoyable environment for all players.
                    </Typography>
                </Box>

                {/* Rules Cards */}
                <Grid container spacing={3}>
                    {sections.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    background:
                                        "rgba(20,20,20,0.82)",
                                    border: "1px solid rgba(255,215,134,0.15)",
                                    borderRadius: "18px",
                                    p: { xs: 3, md: 4 },
                                    backdropFilter: "blur(6px)",
                                    transition: "0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        border:
                                            "1px solid rgba(255,215,134,0.35)",
                                        boxShadow:
                                            "0 0 25px rgba(255,215,134,0.08)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 2,
                                        gap: 1.5,
                                    }}
                                >
                                    {section.icon}

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: "#ffd786",
                                            fontWeight: 700,
                                            fontSize: {
                                                xs: "1.1rem",
                                                md: "1.3rem",
                                            },
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color: "#d6d6d6",
                                        lineHeight: 1.9,
                                        fontSize: {
                                            xs: "0.95rem",
                                            md: "1rem",
                                        },
                                    }}
                                >
                                    {section.content}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Footer */}
                <Divider
                    sx={{
                        borderColor: "rgba(255,255,255,0.08)",
                        my: 6,
                    }}
                />

                <Box textAlign="center">
                    <Typography
                        sx={{
                            color: "#8b8b8b",
                            fontSize: {
                                xs: "0.8rem",
                                md: "0.9rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        Last Updated: May 2026
                        <br />
                        Critical Conquer © All Rights Reserved
                        <br />
                        For support and concerns, please contact us through our
                        official Discord server.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Terms;